import { BaseViewStore } from 'core/stores';
import { action, observable, makeObservable } from 'mobx';
import { roles, shipmentInfoStatusEnum, sourceOfChange } from 'common/constants';
import { FulfillmentOrderStore, FulfillmentOrderProcessingStore } from 'modules/order/stores';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import {
	onPrintLabelStatuses,
	onPrintPackingSlipStatuses,
	fulfillmentOrderStatus,
	channelType,
} from 'common/constants';
import { notificationError, isSeller, isSome, handleVoidLabelResponseNotification } from 'core/utils';
import { ModalParams } from 'core/models';
import { createFileUrl, isSuperAdminOrganization } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';
import {
	AssignCarrierServiceAndWarehouse,
	ChangeShipping,
	EditOrder,
	MarkAsShipped,
	NeverShipped,
	OnHold,
	SplitFulfillmentOrder,
	VoidLabel,
} from '../pages';

class OrderDetailViewStore extends BaseViewStore {
	@observable canCancel = false;
	@observable fulfillmentOrder = null;
	@observable activeShipmentInfo = null;
	@observable actionMenuOpen = false;
	@observable shouldDisplayActionButton = false;
	@observable hasLabel = false;
	@observable selectedItem;
	@observable editFulfillmentOrderSplit = false;
	reload = false;
	openPrintModal = false;
	pdfURL = null;
	isPrintingPackingSlipAllowed = null;
	fulfillmentOrderItems = null;
	itemPrice = 0;
	order = null;
	hasActiveShipment = false;
	fulfillmentOrderStatus;
	packingSlipStore = null;
	isBuyShipping = false;
	showVoidOnChannel = false;
	isMultiItem = false;
	isReentered = false;
	orderAddressData = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.packingSlipStore = new PackingSlipStore(rootStore);
		this.rootStore.viewStore.modes = [];
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSeller = isSeller(rootStore);
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.isWarehouseAdministrator =
			_.findIndex(
				rootStore.app.baasic.userHandler.user.user.roles,
				role => role === roles.warehouseAccountOwner || role === roles.warehouseAdministrator
			) != -1;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		await this.getFulfillmentOrder(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'shippingCoreCountry',
				'shippingCoreCountryState',
				'carrier',
				'carrierService',
				'carrierService.carrierType',
				'cancelReason',
				'originalFulfillmentOrder',
				'seller',
				'seller.sellerOrganizationInfo',
				'warehouseOrganization',
				'warehouseOrganization.convertWarehouses',
				'warehouseOrganization.warehouseOrganizationInfo',
				'warehouseOrganization.warehouseOrganizationInfo.address',
				'warehouseOrganization.warehouseOrganizationInfo.contact',
				'warehouseOrganization.warehouseOrganizationInfo.address.coreCountry',
				'warehouseOrganization.warehouseOrganizationInfo.address.coreCountryState',
				'onHoldReason',
				'replacementReason',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.orderLine',
				'fulfillmentOrderItems.orderLine.product',
				'fulfillmentOrderItems.product',
				'shipmentInfos',
				'fulfillmentOrderStatus',
				'fulfillmentOrderErrors',
				'order',
				'order.orderLines',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
				'order.confirmationType',
				'order.carrier',
				'order.channel',
				'order.channel.channelType',
				'order.channel.timezone',
				'order.orderStatus',
				'order.orderType',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
			],
		});
		this.resume();
		if (this.openPrintModal) {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
		this.calculateItemPrice();
		this.order = this.fulfillmentOrder.order;
	};

	@action.bound
	getWarehouseCodesForDisplay(item) {
		let code = _.find(item.warehouseOrganization.convertWarehouses, convertWarehouse => {
			return (
				convertWarehouse.channelId == item.order.channelId && convertWarehouse.warehouseId == item.assignedWarehouseId
			);
		});

		if (isSome(code)) {
			return code.name;
		} else {
			return '-';
		}
	}

	@action.bound
	getFulfillmentOrder = async (id, options) => {
		await this.fulfillmentOrderStore.getFulfillmentOrder(id, options);
		this.fulfillmentOrder = this.fulfillmentOrderStore.item;
		this.fulfillmentOrderItems = this.fulfillmentOrder.fulfillmentOrderItems;
		this.selectedItem = this.fulfillmentOrder;
		//If there is any address data on FO show those data
		this.orderAddressData = this.fulfillmentOrder;
		this.hasLabel = _.find(this.fulfillmentOrder.shipmentInfos, item => {
			return (
				(isSome(item.label) && item.status == shipmentInfoStatusEnum.active) ||
				((this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.shippedExternally ||
					this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) &&
					item.status == shipmentInfoStatusEnum.active)
			);
		});
		this.isBoxRequired =
			this.fulfillmentOrder.fulfillmentOrderItems.length > 1 ||
			(this.fulfillmentOrder.fulfillmentOrderItems[0] && this.fulfillmentOrder.fulfillmentOrderItems[0].quantity > 1);
		this.isVendorOrder = this.fulfillmentOrder.order.channel.channelType.name === channelType.amazonVendor;
		this.isLtlOrder =
			this.selectedItem.order.channel.name === 'LTL Freight' || this.selectedItem.order.requiredCarrierService == 'LTL';

		this.editFulfillmentOrderSplit =
			(this.fulfillmentOrder.isSplit ||
				(this.fulfillmentOrder.originalFulfillmentOrder != null &&
					this.fulfillmentOrder.originalFulfillmentOrder.isSplit)) &&
			!this.hasLabel;

		this.isPrintingPackingSlipAllowed =
			this.fulfillmentOrder.seller.sellerOrganizationInfo.printPackingSlip &&
			_.findIndex(onPrintPackingSlipStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;

		const isPrintingAllowed =
			_.findIndex(onPrintLabelStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;
		if (
			// Used for rendering Tracking Number and Is Buy Shipping flag in the Header. Reprint is not available.
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.replaced ||
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.reassigned
		) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
		}
		if (isPrintingAllowed) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
			if (this.activeShipmentInfo != null && this.activeShipmentInfo.hasOwnProperty('label')) {
				if (this.activeShipmentInfo.label != null) {
					this.pdfURL = createFileUrl(this.activeShipmentInfo.label);
				}
			}
		} else {
			this.pdfURL = null;
		}
		this.getLocale();
		if (
			this.fulfillmentOrder.onHoldReason != null &&
			this.fulfillmentOrder.onHoldReason.abrv == 'to%20be%20cancelled'
		) {
			this.canCancel = true;
		} else {
			this.canCancel = false;
		}
		this.hasActiveShipment = this.activeShipmentInfo != null;
		this.fulfillmentOrderStatus = this.selectedItem.fulfillmentOrderStatus.abrv;
		this.isBuyShipping = this.activeShipmentInfo != null && this.activeShipmentInfo.isBuyShipping;
		this.isReentered = this.fulfillmentOrder.newShipByDate >= this.fulfillmentOrder.shipByDate;
	};

	@action.bound
	printLabel = async () => {
		this.openPrintModal = true;
		if (this.fulfillmentOrder.fulfillmentOrderStatus.abrv !== fulfillmentOrderStatus.exitScan) {
			await this.shipmentInfoStore.reprintLabel(this.activeShipmentInfo.id, false);
			await this.initialize();
		} else {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
	};

	@action.bound
	printPackingSlip = async () => {
		this.suspend();
		try {
			await this.packingSlipStore.generatePdf(this.fulfillmentOrder.id);
			const pdfResponse = this.packingSlipStore.response;
			if (!pdfResponse.printedWithPrintNode) {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				printJS(fileURL);
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.PACKINGSLIP.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PACKINGSLIP.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	calculateItemPrice() {
		let itemPrice = 0;
		_.each(this.fulfillmentOrder.order.orderLines, orderLine => {
			itemPrice += orderLine.unitPrice * orderLine.quantity;
		});
		this.itemPrice = itemPrice.toFixed(2);
	}

	@action.bound
	openVoidLabelConfirmModal = async (plainLabelVoiding, reoptimizeLabelVoiding) => {
		if (!plainLabelVoiding && this.isBuyShipping) {
			this.openConfirmationModal(
				this.getVoidLabelConfirmModalProps(reoptimizeLabelVoiding, [
					'ORDERVIEW.VOIDLABELONCHANNEL',
					{ fulfillmentOrderId: this.selectedItem.fulfillmentOrderId },
				])
			);
		} else if (plainLabelVoiding) {
			if (this.isBuyShipping) {
				this.openModal({
					onSuccess: async () => await this.initialize(),
					data: { selectedItemId: this.selectedItem.id },
					component: VoidLabel,
				});
			} else
				this.openConfirmationModal(
					this.getVoidLabelConfirmModalProps(reoptimizeLabelVoiding, [
						'ORDERVIEW.VOIDLABEL',
						{ fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId },
					])
				);
		} else if (reoptimizeLabelVoiding) {
			await this.reoptimizeOrder(false);
		}
	};

	openOnHoldModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: OnHold,
		});
	};

	@action.bound
	openEditOrderModal = () => {
		this.openModal(this.getEditOrderModalProps());
	};

	getEditOrderModalProps = () => {
		return {
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id, parentView: this },
			component: EditOrder,
		};
	};

	@action.bound
	resetPrintingData = async () => {
		this.activeShipmentInfo = null;
		this.pdfURL = null;
	};

	@action.bound
	cancelFulfillmentOrder = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.cancelFulfillmentOrder(
				this.fulfillmentOrder,
				sourceOfChange.cancelFulfillmentOrderList
			);
			this.rootStore.notificationStore.success([
				'ORDERDETAILVIEW.FULFILLMENTORDERCANCELED',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};

	@action.bound
	getLocale = () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		// Required to be state due to the error log template which uses the store format and timezone
		this.format = localeData.longDateFormat('L');
		this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	};

	@action.bound
	pullLabel = async () => {
		this.suspend();
		try {
			const response = await new FulfillmentOrderProcessingStore(this.rootStore).createFulfillmentShipment(
				this.fulfillmentOrder.id,
				sourceOfChange.pullLabelOrderDetails,
				false
			);
			if (response.shipmentInfo) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.PULLLABELSUCCESS',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else if (response.message || response.errors) {
				// Error notification after shipment PII data is validated as false on the backend, before create shipment request.
				this.rootStore.notificationStore.error([
					response.message ? response.message : 'ORDERVIEW.PULLLABELERROR',
					{
						id: this.fulfillmentOrder.fulfillmentOrderId,
						notification: response.errors ? response.errors.join(' ') : null,
					},
				]);
			} else if (response.shipmentData.notifications) {
				// Error notification after shipment data is validated as true, but error occured on create shipment request.
				this.rootStore.notificationStore.error([
					(response.message = 'ORDERVIEW.PULLLABELERROR'),
					{ id: this.fulfillmentOrder.fulfillmentOrderId, notification: response.shipmentData.notifications.join(' ') },
				]);
			} else {
				// Generic error notification if no specific error message has been caught.
				this.rootStore.notificationStore.error([
					'ORDERVIEW.PULLLABELERROR',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				err.data.message ? err.data.message : 'ORDERVIEW.PULLLABELERROR',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	updateProductionMode = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.toggleProductionMode(
				this.fulfillmentOrder.id,
				!this.fulfillmentOrder.productionMode,
				sourceOfChange.productionModeOrderDetail
			);
			if (!this.fulfillmentOrder.productionMode) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSON',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSOFF',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
			await this.initialize();
		} catch (err) {
			if (err.data != null && err.data.details != null) {
				this.rootStore.notificationStore.error(err.data.details);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	reoptimizeOrder = async (voidOnChannel = false) => {
		this.suspend;
		try {
			await this.fulfillmentOrderStore.reoptimize(
				{
					fulfillmentOrderIds: [this.selectedItem.id],
					voidOnChannel: voidOnChannel,
				},
				sourceOfChange.reoptimizeOrderDetail
			);
			this.rootStore.notificationStore.success([
				'ORDERVIEW.REOPTIMIZEORDERSUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	displayPopoverButton = width => {
		//1165px is equal to 1440 width resolution
		if (width > 1500) {
			this.shouldDisplayActionButton = false;
		} else {
			this.shouldDisplayActionButton = true;
		}
	};

	openAssignmentModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: AssignCarrierServiceAndWarehouse,
		});
	};

	historyLogModal = new ModalParams();

	openChangeShippingModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id, resetPrintingData: async () => await this.resetPrintingData() },
			component: ChangeShipping,
		});
	};

	getProductionModeConfirmModalProps = checked => {
		return {
			onConfirm: async () => await this.updateProductionMode(),
			message: [
				checked ? 'ORDERVIEW.TOGGLEPRODUCTIONMODEON' : 'ORDERVIEW.TOGGLEPRODUCTIONMODEOFF',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			],
		};
	};

	getCancelConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.cancelFulfillmentOrder(),
			message: ['ORDERVIEW.CANCELFULFILLMENTORDER', { id: this.fulfillmentOrder.fulfillmentOrderId }],
		};
	};

	openNeverShippedModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItem: this.selectedItem },
			component: NeverShipped,
		});
	};

	openSplitFulfillmentOrderModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: {
				selectedItemId: this.selectedItem.id,
				orderId: this.selectedItem.orderId,
				isEdit: this.editFulfillmentOrderSplit,
			},
			component: SplitFulfillmentOrder,
			className: 'ReactModal--lrg',
		});
	};

	getReoptimizeConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.openVoidLabelConfirmModal(false, true),
			message: ['ORDERVIEW.REOPTIMIZEORDERCONFIRM', { id: this.selectedItem.fulfillmentOrderId }],
		};
	};

	getVoidLabelConfirmModalProps = (reoptimizeLabelVoiding, message) => {
		return {
			onConfirm: async () => {
				this.suspend();
				try {
					if (this.rootStore.viewStore.selectedConfirmModal.data.reoptimizeLabelVoiding) {
						await this.reoptimizeOrder(true);
					} else {
						let voidLabelOption = this.activeShipmentInfo.isBuyShipping ? 1 : 0;
						const response = await this.shipmentInfoStore.voidLabel(
							{ fulfillmentOrderId: this.fulfillmentOrder.id },
							voidLabelOption,
							sourceOfChange.voidActiveLabelConfirmModal
						);

						handleVoidLabelResponseNotification(this.rootStore, response, this.fulfillmentOrder.fulfillmentOrderId);

						this.pdfURL = null;
						this.initialize();
					}
				} catch (err) {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
				this.resume();
			},
			onCancel: async () => {
				if (this.rootStore.viewStore.selectedConfirmModal.data.reoptimizeLabelVoiding) {
					await this.reoptimizeOrder(false);
				}
			},
			message: message,
			data: { reoptimizeLabelVoiding: reoptimizeLabelVoiding },
		};
	};

	getPullLabelConfirmModalProps = () => {
		return {
			onConfirm: () => this.pullLabel(),
			message: [
				['staging', 'test', 'development'].includes(process.env.NODE_ENV) && this.fulfillmentOrder.productionMode
					? 'PRODUCTIONPULLLABEL'
					: 'ORDERVIEW.PULLLABELCONFIRM',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			],
		};
	};

	openMarkAsShippedModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: MarkAsShipped,
		});
	};
}

export default OrderDetailViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/OrderDetailViewStore.js